import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import Paginator from 'components/paginator';
import { flexCenter } from '@seedlang/style_mixins';
import Text from 'components/text';
import { AppUI, ExerciseUI } from '@seedlang/state';
import autobind from 'autobind-decorator';
import { isPresent, isBlank } from '@seedlang/utils';
import WorksheetFilter from 'components/worksheet/worksheet_filter';
import Spinner from 'components/spinner';
import { Theme } from '@seedlang/constants';
import cx from 'classnames';
import { LockIcon } from '@seedlang/icons';
import Stack from 'components/layout/stack';
import { Link } from 'react-router';
import ReactTooltip from 'react-tooltip';
import { LabelWrapper, Label } from 'components/worksheet/worksheet_grid_item';

const Wrapper = styled.div`
  padding-top: ${props => props.paddingTop};
`;

const TableWrapper = styled.div`
  flex: 1;
`;

const Table = styled.div`
  border-radius: ${props => props.borderRadius};
  overflow: hidden;
`;

const Row = styled.div`
  ${flexCenter()}
  position: relative;
  background: ${props => props.header ? '#CCC' : '#FFF'};
  cursor: ${props => props.header ? 'default' : 'pointer'};
  border-radius: ${props => props.borderRadius};
  margin: ${props => props.margin};
  padding: ${props => props.padding || '10px'};
  &:hover {
    background: ${props => props.header ? '#CCC' : 'whitesmoke'};
    .grayscale {
      filter: none;
    }
  }
`;

const LinkRow = styled(Link)`
  ${flexCenter()}
  position: relative;
  background: #FFF;
  cursor: pointer;
  border-radius: ${props => props.borderRadius};
  margin: ${props => props.margin};
  padding: ${props => props.padding || '10px'};
  text-decoration: none;
  color: inherit;
  &:hover {
    background: whitesmoke;
    text-decoration: none;
    color: inherit;
    .grayscale {
      filter: none;
    }
  }
`;

const Episode = styled.div`
  flex: 1;
  min-width: 0;
`;

const Thumbnail = styled.div`
  height: 60px;
  margin-right: 10px;
  flex-shrink: 0;
  position: relative;
  img {
    border-radius: 10px;
    height: 100%;
    width: auto;
    cursor: pointer;
  }
  .grayscale {
    filter: gray;
    filter: grayscale(1);
    transition: filter 0.3s ease;
  }
`;

const LockIconWrapper = styled.div`
  z-index: ${Theme.z['foreground']};
  width: 20px;
  height: 20px;
  border-radius: 8px;
  background: #777777;
  position: absolute;
  top: 1px;
  left: 1px;
  pointer-events: none;
  .icon-wrapper {
    svg {
      fill: #FFF;
      width: 8px;
      position: absolute;
      left: 6px;
      top: 4px;
    }
  }
`;

const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  color: #a8a8a8;
`;

const DateAndDuration = styled.div`
  color: ${Theme.darkGray};
  font-size: 13px;
  line-height: 20px;
`;

const CompletionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: 5px;
  cursor: pointer;
  color: ${props => props.completed ? Theme.green : Theme.gray};
  font-size: inherit;
  vertical-align: initial;
  
  &:hover {
    opacity: 0.8;
  }

  i {
    color: inherit;
    margin-top: 1px;
  }
`;

@observer
class WorksheetTable extends React.Component {
  @observable searchQuery = null;
  @observable dateSort = '-worksheets.published_at';

  @computed get paginatorIds() {
    const params = {groupId: this.props.params?.groupId};
    if (this.selectedTagIds) {
      params['tag_ids'] = this.selectedTagIds;
    }
    return params;
  }

  @computed get sortParameter() {
    const showFreeWorksheetsFirst = !ExerciseUI.user.isMember || (AppUI.vocabBlockedUntilMigrated || AppUI.exercisesBlockedUntilMigrated);
    return [
      showFreeWorksheetsFirst && '-access_without_membership',
      '-pinned',
      this.dateSort,
    ].filter(Boolean).join(',');
  }

  constructor(props) {
    super(props);
    this.getWorksheets();
  }

  componentWillUnmount() {
    this.props.store.clearIndexData();
  }

  @autobind getWorksheets() {
    this.props.store.clearFilter();
    if (isPresent(this.props.store.limit)) {
      this.props.store.setLimit();
    }
    const params = {
      filters: this.props.defaultWorksheetFilter || {},
      sort: this.sortParameter,
      queryStrings: {
        show_all: true,
        podcast: this.props.route?.name === 'podcast.index',
        videos: this.props.route?.name === 'videos.index',
      },
    };

    if (this.searchQuery) {
      params['filters']['worksheets.name'] = `~${this.searchQuery}~`;
    }

    this.props.store.setPage(1);
    this.props.store.getIndex(params);
  }

  @autobind onSearchQueryChange(val) {
    this.searchQuery = val;
    clearInterval(this.interval);
    if (isBlank(val)) {
      this.getWorksheets();
    }
    if (isBlank(val) || val.length > 1) {
      this.interval = setTimeout(this.getWorksheets, 500);
    }
  }

  @autobind toggleSort(field) {
    if (this.dateSort.endsWith(field)) {
      this.dateSort = this.dateSort.startsWith('-') ? field : `-${field}`;
    } else {
      this.dateSort = `-${field}`;
    }
    this.getWorksheets();
  }

  @autobind getItemRoute(item) {
    if (this.props.getItemRoute) {
      return this.props.getItemRoute(item);
    }
    return { pathname: '#' };
  }

  @autobind toggleCompleted(e, item) {
    e.preventDefault();
    e.stopPropagation();

    if (this.props.onToggleCompleted) {
      this.props.onToggleCompleted(item);
    }
  }

  formatDuration = seconds => {
    if (!seconds) return '';

    const minutes = Math.ceil(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return remainingMinutes > 0
        ? `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min`
        : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes} min`;
  };

  render() {
    const { store, showFilters = true } = this.props;

    return (
      <Wrapper paddingTop={AppUI.layout.isMobile ? '15px' : null}>
        <Stack gap='sm' alignItems='stretch'>
          {showFilters && (
            <WorksheetFilter
              showFilters
              onUpdateFilter={this.getWorksheets}
              onSearchQueryChange={this.onSearchQueryChange}
              searchQuery={this.searchQuery}
              routeName={this.props.route?.name}
              defaultWorksheetFilter={this.props.defaultWorksheetFilter}
              onToggleSort={this.toggleSort}
              sortDirection={this.dateSort.startsWith('-') ? 'desc' : 'asc'}
            />
          )}
          <TableWrapper>
            <Table borderRadius={AppUI.layout.isMobile ? '0' : '10px'}>
              {store.requestCounter > 0 && (
                <Spinner />
              )}

              {store.requestCounter === 0 && !store.hasIndexData && (
                <Row borderRadius={AppUI.layout.isDesktop ? '0 0 10px 10px' : ''}>
                  <Text>No episodes found</Text>
                </Row>
              )}

              {store.indexData.map((item, index) => {
                const hasAccess = ExerciseUI.hasWorksheetAccess(item);
                const showFreeLabel = item.published
                  && item.accessWithoutMembership
                  && (!ExerciseUI.user.isMember || AppUI.isPaymentMigrationStartable);

                // Get image URL with fallback to podcast image
                const getImageUrl = () => {
                  if (isPresent(item.imageUrl)) {
                    return item.imageUrl;
                  }
                  // Fallback to podcast image if worksheet has no image
                  if (item.podcastEpisode?.podcast?.podcastImage?.image?.url) {
                    return item.podcastEpisode.podcast.podcastImage.image.url;
                  }
                  return null;
                };

                // Parse HTML to extract plain text
                const getCleanDescription = htmlDescription => {
                  if (!htmlDescription) return '';

                  // Create a temporary div to parse HTML
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = htmlDescription;
                  return tempDiv.textContent || tempDiv.innerText || '';
                };

                const imageUrl = getImageUrl();
                const cleanDescription = getCleanDescription(item.description);
                const duration = item.podcastEpisode?.duration;

                return (
                  <LinkRow
                    key={item.id}
                    to={this.getItemRoute(item)}
                    padding={AppUI.layout.isMobile ? '10px 10px 5px 10px' : '10px'}
                    borderRadius={index === store.indexData.length - 1 && !AppUI.layout.isMobile ? '0 0 10px 10px' : ''}
                  >
                    {!hasAccess && !showFreeLabel && (
                      <LockIconWrapper>
                        <LockIcon />
                      </LockIconWrapper>
                    )}
                    <div style={{display: 'flex', flex: 1, minWidth: 0}}>
                      {imageUrl && (
                        <Thumbnail>
                          {showFreeLabel && (
                            <LabelWrapper>
                              <Label background={AppUI.site.secondaryAccentColor}>
                                FREE
                              </Label>
                            </LabelWrapper>
                          )}
                          <img
                            alt=''
                            src={imageUrl}
                            className={cx({grayscale: !hasAccess && !showFreeLabel})}
                          />
                        </Thumbnail>
                      )}
                      <Episode>
                        <Text bold>
                          {!AppUI.layout.isMobile ? (
                            <>
                              <CompletionButton
                                type='button'
                                completed={item.overrideCompleted}
                                onClick={e => this.toggleCompleted(e, item)}
                                aria-label={item.overrideCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                data-tip
                                data-for={`completion-tooltip-${item.id}`}
                              >
                                <i className='fa fa-check-circle' />
                              </CompletionButton>
                              <ReactTooltip
                                id={`completion-tooltip-${item.id}`}
                                place='top'
                                type='dark'
                                effect='solid'
                              >
                                {item.overrideCompleted ? 'Mark as Incomplete' : 'Mark as Completed'}
                              </ReactTooltip>
                            </>
                          ) : (
                            <i
                              className='fa fa-check-circle'
                              style={{
                                color: item.overrideCompleted ? Theme.green : Theme.gray,
                                marginRight: '5px',
                                verticalAlign: 'initial',
                              }}
                            />
                          )}
                          {item.name}
                        </Text>
                        {!AppUI.layout.isMobile && (item.podcastEpisode?.subtitle || cleanDescription) && (
                          <Description>
                            {item.podcastEpisode?.subtitle || cleanDescription}
                          </Description>
                        )}
                        <DateAndDuration>
                          {item.publishedAt ? item.publishedAt.fullTextFormattedDateWithFullYear : null}
                          {duration && ` • ${this.formatDuration(duration)}`}
                        </DateAndDuration>
                      </Episode>
                    </div>
                  </LinkRow>
                );
              })}

              <Paginator
                onClickScrollToTop
                withSpinner
                store={store}
                ids={this.paginatorIds}
                queryStrings={{
                  show_all: true,
                  podcast: this.props.route?.name === 'podcast.index',
                  videos: this.props.route?.name === 'videos.index',
                }}
                margin='20px 0'
              />
            </Table>
          </TableWrapper>
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(WorksheetTable);